import React, { useState, useEffect, useRef } from 'react';
import { GeoAutocomplete } from './GeoAutocomplete.jsx';
import { TagPicker } from './TagPicker.jsx';
import DuplicateWarning from './DuplicateWarning.jsx';
import { checkDuplicates, keepRawHandle } from '../api/leads.js';
import { APPS } from '../constants/apps.js';
import { FOUND_SOURCES } from '../../../shared/leadOptions/index.js';
import { PLATFORM_LABEL_SHORT } from '../constants/platforms.js';
import { normalizeHandle } from '../utils.js';

export const CreateBloggerModal = ({
    open,
    loading,
    form,
    isEditing,
    leadId = null,
    onClose,
    onFieldChange,
    onSubmit,
    duplicateWarning = null,
    submitError = null,
    claimIds = new Set(),
    onToggleClaim,
    onToggleAllClaims,
    onClaim,
    claimingDuplicate = false,
    extraAccounts: externalExtraAccounts,
    onExtraAccountsChange,
    payment: externalPayment,
    onPaymentChange,
}) => {
    if (!open) return null;

    const [internalExtraAccounts, setInternalExtraAccounts] = useState([]);
    const extraAccounts = externalExtraAccounts ?? internalExtraAccounts;
    const setExtraAccounts = onExtraAccountsChange ?? setInternalExtraAccounts;

    const [newAccPlatform, setNewAccPlatform] = useState('');
    const [newAccHandle, setNewAccHandle] = useState('');

    const [showPayment, setShowPayment] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('paypal');
    const [paymentDetails, setPaymentDetails] = useState('');

    const [dupCheckResult, setDupCheckResult] = useState(null);
    const [dupChecking, setDupChecking] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setDupCheckResult(null);
            setDupChecking(false);
            setInternalExtraAccounts([]);
            setNewAccPlatform('');
            setNewAccHandle('');
            setShowPayment(false);
            setPaymentMethod('paypal');
            setPaymentDetails('');
            if (debounceRef.current) clearTimeout(debounceRef.current);
        }
    }, [open]);

    const blockedByDupCheck = !isEditing && Boolean(dupCheckResult?.isDuplicate || dupCheckResult?.rejected?.length);

    const handleSubmit = (event) => {
        event.preventDefault();
        const igHandle = normalizeHandle(form.instagram, 'instagram');
        const ttHandle = normalizeHandle(form.tiktok, 'tiktok');
        const derivedName =
            form.name?.trim() ||
            igHandle ||
            ttHandle ||
            (form.email?.includes('@') ? form.email.split('@')[0].trim() : '') ||
            '';
        const payload = {
            ...form,
            name: derivedName,
            instagram: keepRawHandle(form.instagram, 'instagram'),
            tiktok: keepRawHandle(form.tiktok, 'tiktok'),
            youtube: keepRawHandle(form.youtube, 'youtube'),
            facebook: keepRawHandle(form.facebook, 'facebook'),
            threads: keepRawHandle(form.threads, 'threads'),
            x: keepRawHandle(form.x, 'x'),
            extraAccounts: extraAccounts
                .map(a => ({ ...a, handle: keepRawHandle(a?.handle, a?.platform) }))
                .filter(a => a && a.handle),
            payment: showPayment ? { method: paymentMethod, details: paymentDetails } : null,
        };
        onSubmit(payload);
    };

    const handleBackdropClick = () => {};

    const handleIdentifierChange = (field, value) => {
        onFieldChange(field, value);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(async () => {
            const currentForm = { ...form, [field]: value };
            const { instagram, tiktok, email } = currentForm;

            if (!instagram && !tiktok && !email) {
                setDupCheckResult(null);
                return;
            }

            setDupChecking(true);
            try {
                const result = await checkDuplicates({ instagram, tiktok, email, ...(leadId ? { excludeId: leadId } : {}) });
                setDupCheckResult(result);
            } catch (err) {
                console.error('Dedup check failed', err);
                setDupCheckResult(null);
            } finally {
                setDupChecking(false);
            }
        }, 500);
    };

    const sectionTitle = {
        fontSize: '11px',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    };

    const currencyPillStyle = (active) => ({
        padding: '5px 12px',
        borderRadius: '6px',
        border: active ? '1px solid var(--purple-60)' : '1px solid var(--neutral-40)',
        background: active ? 'rgba(99, 102, 241, 0.1)' : 'var(--neutral-0)',
        color: active ? 'var(--purple-60)' : 'var(--text-muted)',
        fontSize: '12px',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        transition: 'all 0.15s',
    });

    return (
        <div className='manager-modal-backdrop' role='presentation' onClick={handleBackdropClick}>
            <div className='manager-modal' style={{ width: '560px' }} role='dialog' aria-modal='true'>
                <header className='manager-modal__header'>
                    <h2>{isEditing ? 'Редактировать блогера' : 'Добавить блогера'}</h2>
                    <button type='button' className='manager-modal__close' onClick={onClose} disabled={loading} aria-label='Закрыть'>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                </header>

                <form onSubmit={handleSubmit} className='manager-modal__body' style={{ gap: '10px', padding: '16px 24px' }}>
                    <div className='manager-modal__grid' style={{ gap: '8px' }}>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.name} onChange={e => onFieldChange('name', e.target.value)} disabled={loading} placeholder='Никнейм (авто из ссылки)' autoFocus />
                        </div>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.realName || ''} onChange={e => onFieldChange('realName', e.target.value)} disabled={loading} placeholder='Имя' />
                        </div>
                    </div>

                    <div className='manager-modal__grid' style={{ gap: '8px' }}>
                        <div className='manager-modal__field'>
                            <input type='email' value={form.email} onChange={e => handleIdentifierChange('email', e.target.value)} disabled={loading} placeholder='Email' />
                        </div>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.phone || ''} onChange={e => onFieldChange('phone', e.target.value)} disabled={loading} placeholder='Телефон' />
                        </div>
                    </div>

                    <div className='manager-modal__field'>
                        <GeoAutocomplete value={form.geo || ''} onChange={(value) => onFieldChange('geo', value)} disabled={loading} />
                    </div>

                    <div className='manager-modal__grid' style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.instagram} onChange={e => handleIdentifierChange('instagram', e.target.value)} disabled={loading} placeholder='Instagram' />
                        </div>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.tiktok} onChange={e => handleIdentifierChange('tiktok', e.target.value)} disabled={loading} placeholder='TikTok' />
                        </div>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.youtube} onChange={e => onFieldChange('youtube', e.target.value)} disabled={loading} placeholder='YouTube' />
                        </div>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.facebook || ''} onChange={e => onFieldChange('facebook', e.target.value)} disabled={loading} placeholder='Facebook' />
                        </div>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.threads || ''} onChange={e => onFieldChange('threads', e.target.value)} disabled={loading} placeholder='Threads' />
                        </div>
                        <div className='manager-modal__field'>
                            <input type='text' value={form.x || ''} onChange={e => onFieldChange('x', e.target.value)} disabled={loading} placeholder='X' />
                        </div>
                    </div>

                    <div style={sectionTitle}>Амбассадорские аккаунты</div>
                    <div className='lead-edit__extra-accounts'>
                        {extraAccounts.map((acc, i) => (
                            <span key={i} className='lead-edit__extra-chip'>
                                <span className='lead-edit__extra-chip-label'>{PLATFORM_LABEL_SHORT[acc.platform] || acc.platform}</span>
                                {acc.handle}
                                <button type='button' className='lead-edit__extra-chip-remove' onClick={() => setExtraAccounts(prev => prev.filter((_, j) => j !== i))} disabled={loading}>×</button>
                            </span>
                        ))}
                        {extraAccounts.length < 5 && !newAccPlatform && (
                            <select className='lead-edit__extra-add-select' value='' onChange={e => { if (e.target.value) { setNewAccPlatform(e.target.value); setNewAccHandle(''); } }} disabled={loading}>
                                <option value=''>+</option>
                                <option value='instagram'>IG</option>
                                <option value='tiktok'>TT</option>
                                <option value='youtube'>YT</option>
                                <option value='facebook'>FB</option>
                                <option value='threads'>Threads</option>
                                <option value='x'>X</option>
                            </select>
                        )}
                    </div>
                    {newAccPlatform && (
                        <div className='lead-edit__extra-input-row'>
                            <span className='lead-edit__extra-chip-label'>{PLATFORM_LABEL_SHORT[newAccPlatform] || newAccPlatform}</span>
                            <input className='form-input' type='text' placeholder='@username' autoFocus value={newAccHandle} onChange={e => setNewAccHandle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newAccHandle.trim()) { e.preventDefault(); setExtraAccounts(prev => [...prev, { platform: newAccPlatform, handle: newAccHandle.trim() }]); setNewAccPlatform(''); setNewAccHandle(''); } }} disabled={loading} />
                            <button type='button' className='ghost-button ghost-button--xs' disabled={!newAccHandle.trim() || loading} onClick={() => { setExtraAccounts(prev => [...prev, { platform: newAccPlatform, handle: newAccHandle.trim() }]); setNewAccPlatform(''); setNewAccHandle(''); }}>Добавить</button>
                            <button type='button' className='lead-edit__extra-chip-remove' onClick={() => { setNewAccPlatform(''); setNewAccHandle(''); }}>×</button>
                        </div>
                    )}

                    <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '4px', height: '14px', borderRadius: '2px', background: 'var(--purple-60)', flexShrink: 0 }} />
                        Проверка на уникальность
                    </div>
                    <div style={{ minHeight: '20px' }}>
                        {dupChecking && (
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Проверяем...</span>
                        )}
                        {!dupChecking && dupCheckResult?.rejected?.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#f87171', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {dupCheckResult.rejected.map((r) => (
                                    <span key={r.field}>{r.reason}</span>
                                ))}
                            </div>
                        )}
                        {!dupChecking && dupCheckResult && !dupCheckResult.isDuplicate && !dupCheckResult.rejected?.length && (
                            <span style={{ fontSize: '12px', color: '#4ade80', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '14px' }}>✓</span> Дублей не найдено
                            </span>
                        )}
                        {!dupChecking && dupCheckResult && dupCheckResult.isDuplicate && (
                            <DuplicateWarning
                                duplicates={dupCheckResult.duplicates}
                                claimIds={new Set()}
                                onToggleClaim={() => {}}
                                onToggleAllClaims={() => {}}
                                onClaim={() => {}}
                                claiming={false}
                                showRowColumn={false}
                                compact
                            />
                        )}
                        {!dupChecking && !dupCheckResult && (
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                Заполните Instagram, TikTok или Email для проверки
                            </span>
                        )}
                    </div>

                    {submitError && (
                        <div style={{ fontSize: '12px', color: '#f87171' }}>{submitError}</div>
                    )}

                    {duplicateWarning && (
                        <DuplicateWarning
                            duplicates={[duplicateWarning]}
                            claimIds={claimIds}
                            onToggleClaim={onToggleClaim || (() => {})}
                            onToggleAllClaims={onToggleAllClaims || (() => {})}
                            onClaim={onClaim || (() => {})}
                            claiming={claimingDuplicate}
                            showRowColumn={false}
                        />
                    )}

                    <div className='manager-modal__field'>
                        <label>Комментарий</label>
                        <textarea value={form.comment || ''} onChange={e => onFieldChange('comment', e.target.value)} disabled={loading} placeholder='Комментарий...' rows={1} style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                    </div>

                    <div className='manager-modal__field'>
                        <label>Приложение</label>
                        <select value={form.application || ''} onChange={e => onFieldChange('application', e.target.value)} disabled={loading}>
                            <option value=''>— не указано —</option>
                            {APPS.map(a => (
                                <option key={a.value} value={a.value}>{a.value}</option>
                            ))}
                        </select>
                    </div>

                    {!isEditing && (
                        <div className='manager-modal__field'>
                            <label>Источник</label>
                            <select value={form.foundSource || ''} onChange={e => onFieldChange('foundSource', e.target.value)} disabled={loading}>
                                <option value=''>— не указано —</option>
                                {FOUND_SOURCES.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {!isEditing && (
                        <div className='manager-modal__field'>
                            <label>Тег</label>
                            <TagPicker value={form.tagLabel || ''} onChange={v => onFieldChange('tagLabel', v)} disabled={loading} />
                        </div>
                    )}

                    <footer className='manager-modal__footer'>
                        <button type='button' className='ghost-button' onClick={onClose} disabled={loading}>Отмена</button>
                        <button type='submit' className='primary-button' disabled={loading || blockedByDupCheck || !(form.name?.trim() || form.instagram?.trim() || form.tiktok?.trim() || form.email?.trim())}>
                            {loading ? (isEditing ? 'Сохраняем...' : 'Добавляем...') : (isEditing ? 'Сохранить' : 'Добавить')}
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    );
};
